import { BaseModel, BaseModelProps, ReactRenderer } from '@mail/3promo';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import modalImage from 'img/freemium/testUnlimitedModal.png';
import modalImageDark from 'img/freemium/testUnlimitedModalDark.png';
import React from 'react';
import { IS_MOBILE_BROWSER, IS_WEBVIEW, USER_EMAIL } from 'reactApp/appHelpers/configHelpers';
import {
    freemiumTestUnlimitedModal,
    isControlFreemium,
    isHardFreemium,
    isSoftFreemium,
} from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { hadFreemiumSubscriptionFromSsr } from 'reactApp/modules/billingData/billingData.ssr.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';

const MODAL_ID = 'freemium-test-unlimited-modal';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/FreemiumTestUnlimitedModal/FreemiumTestUnlimitedModal'));

const SuspendedComponent = () => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent />
        </React.Suspense>
    );
};

export const freemiumTestUnlimitedModalCreator: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        MODAL_ID,
        models.base,
        new BaseModelProps(MODAL_ID, {
            store: new LocalStorageStore(MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(MODAL_ID, SuspendedComponent, render.react),
            templateData: {},
            isModelEnabledCheck: () => !!freemiumTestUnlimitedModal,
            ignoreTestOnRedraw: true,
            ignorePromoShowInterval: true,
            showLimit: 1000,
            closeLimit: 1,
            preloadingImages: [modalImage, modalImageDark],
            onShow: (_model) => {
                // Сохраняем показ в sessionStorage (для текущей вкладки)
                const sessionKey = `${USER_EMAIL || ''}|${MODAL_ID}-session`;
                sessionStorage.setItem(sessionKey, 'true');

                // Увеличиваем счетчик в localStorage
                const countKey = `${MODAL_ID}-count`;
                const showCount = Number(storeHelper.getValue(countKey) || '0');
                storeHelper.setValue(countKey, String(showCount + 1));
            },
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (storage !== EStorageType.home) {
                    reject();
                    return;
                }

                if (!freemiumTestUnlimitedModal) {
                    reject();
                    return;
                }

                if (IS_WEBVIEW || IS_MOBILE_BROWSER) {
                    reject();
                    return;
                }

                if (!isSoftFreemium && !isHardFreemium && !isControlFreemium) {
                    reject();
                    return;
                }

                const hadFreemium = hadFreemiumSubscriptionFromSsr(state);

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);

                if (isBizUser || isCorpUser || isPaidUser || hadFreemium) {
                    reject();
                    return;
                }

                // Проверка localStorage - общее количество показов (максимум 3)
                const countKey = `${MODAL_ID}-count`;
                const showCount = Number(storeHelper.getValue(countKey) || '0');

                if (showCount >= 3) {
                    reject();
                    return;
                }

                // Проверка sessionStorage - показывали ли в этой вкладке (для предотвращения множественных показов)
                const sessionKey = `${USER_EMAIL || ''}|${MODAL_ID}-session`;
                const wasShownInTab = sessionStorage.getItem(sessionKey) === 'true';

                if (wasShownInTab) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
